import {createSelector} from '@reduxjs/toolkit';
import type {
  ForecastModelMode,
  ForecastModelStrategy,
  ForecastPreference,
  ForecastPreferenceField,
} from '.';
import {CommonRootState} from '../types';

export const selectUserUnits = (state: CommonRootState) => {
  return state.userPreference.units;
};

export const selectUserFeelLike = (state: CommonRootState) => {
  return state.userPreference.feelLike;
};

export const selectUserDateTime = (state: CommonRootState) => {
  return state.userPreference.date_time;
};

export const selectForecastModelOrder = (
  state: CommonRootState,
): string[] | null => {
  return state.userPreference.forecastModelOrder ?? null;
};

// The `?? null` / `|| EMPTY_PENDING` fallbacks below are load-bearing, not
// belt-and-braces. The webapp's persistConfig misspells `stateReconciler`
// (apps/webapp/src/store/index.tsx), so redux-persist falls back to
// autoMergeLevel1, which replaces this slice wholesale with what is in
// storage instead of merging it over initialState. An install that predates
// these fields therefore rehydrates without them, and `undefined` would reach
// both the PATCH body (where JSON.stringify drops it) and `pending.includes`.
export const selectForecastModelPins = (
  state: CommonRootState,
): string[] | null => {
  return state.userPreference.forecastModelPins ?? null;
};

export const selectForecastModelMode = (
  state: CommonRootState,
): ForecastModelMode | null => {
  return state.userPreference.forecastModelMode ?? null;
};

export const selectForecastModelStrategy = (
  state: CommonRootState,
): ForecastModelStrategy | null => {
  return state.userPreference.forecastModelStrategy ?? null;
};

/**
 * The whole device-shared preference as one bag. Memoized because consumers
 * compare it by value (useForecastPreferenceSync re-runs its push effect on
 * every change), and a fresh object each render would re-fire that effect.
 */
export const selectForecastPreference = createSelector(
  selectForecastModelOrder,
  selectForecastModelPins,
  selectForecastModelMode,
  selectForecastModelStrategy,
  (order, pins, mode, strategy): ForecastPreference => ({
    order,
    pins,
    mode,
    strategy,
  }),
);

// Shared instance so the selector keeps a stable identity for the (common)
// case of nothing pending; a fresh [] each call would re-fire the push effect
// on every render.
const EMPTY_PENDING: ForecastPreferenceField[] = [];

/** The preference fields holding an edit no server has accepted. */
export const selectForecastPreferencePending = (
  state: CommonRootState,
): ForecastPreferenceField[] => {
  return state.userPreference.forecastPreferencePending || EMPTY_PENDING;
};

const getDeviceLanguage = (_: CommonRootState, deviceLanguage: string) =>
  deviceLanguage;

export const selectRawUserLanguage = (
  state: CommonRootState,
): string | null => {
  return state.userPreference.language;
};

const getDebugLanguageOverride = (state: CommonRootState) =>
  state.user.debugLanguageOverride;

export const selectUserLanguage = createSelector(
  getDebugLanguageOverride,
  selectRawUserLanguage,
  getDeviceLanguage,
  (debugLanguage, userLanguage, deviceLanguage) => {
    return debugLanguage || userLanguage || deviceLanguage || 'en';
  },
  {
    devModeChecks: {identityFunctionCheck: 'never'},
  },
);
